import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Download, Maximize2, X, Shield } from 'lucide-react';
import styles from './MyGatePass.module.css';
import api from '../../api/axios';

const MyGatePass = () => {
  const [gatePass, setGatePass] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchMyGatePass();
  }, []);

  const fetchMyGatePass = async () => {
    try {
      const response = await api.get('/users/gate-pass');
      setGatePass(response.data);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch Gate Pass');
      setLoading(false);
    }
  };

  const handleDownloadQR = () => {
    const svg = document.getElementById('qr-code-svg');
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img.width + 40; // Add some padding
      canvas.height = img.height + 40;
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 20, 20);
      
      const pngFile = canvas.toDataURL("image/png");
      const downloadLink = document.createElement("a");
      downloadLink.download = `GatePass-${gatePass.passId}.png`;
      downloadLink.href = `${pngFile}`;
      downloadLink.click();
    };
    
    img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));
  };

  if (loading) return <div className={styles.container}>Loading your gate pass...</div>;
  if (error) return <div className={styles.container}>
    <div className={styles.header}>
      <h1><Shield size={28} color="#22c55e" /> My Gate Pass</h1>
      <p>{error}</p>
    </div>
  </div>;
  if (!gatePass) return null;

  const isRevoked = gatePass.status === 'Revoked';

  const getImageUrl = (url) => {
    if (!url) return 'https://cdn-icons-png.flaticon.com/512/3135/3135715.png';
    if (url.startsWith('http')) return url;
    return `${import.meta.env.VITE_API_URL.replace(/\/api\/?$/, '')}${url}`;
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1><Shield size={28} color="#22c55e" /> My Gate Pass</h1>
        <p>Your digital gate pass for quick and secure entry to the society.</p>
      </div>

      <div className={styles.passCard}>
        <div className={styles.cardHeader}>
          <img 
            src={getImageUrl(gatePass.resident?.profileImage)} 
            alt="Profile" 
            className={styles.avatar} 
          />
          <div className={styles.userInfo}>
            <h2>{gatePass.resident?.fullName || gatePass.name}</h2>
            <p>Flat {gatePass.flat} • Block {gatePass.block || 'N/A'}</p>
          </div>
        </div>

        <div className={styles.qrSection}>
          <div className={styles.qrWrapper}>
            {isRevoked ? (
              <div style={{ width: 180, height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', color: '#94a3b8', borderRadius: 8 }}>
                Revoked
              </div>
            ) : (
              <QRCodeSVG 
                id="qr-code-svg"
                value={gatePass.qrToken} 
                size={180} 
                level="H"
                includeMargin={true}
              />
            )}
          </div>
          <div className={styles.passId}>{gatePass.passId}</div>
        </div>

        <div className={styles.details}>
          <div style={{ display: 'flex', gap: '24px' }}>
            <div style={{ flex: 1 }}>
              <div className={styles.detailGroup}>
                <label>Vehicle</label>
                <strong>{gatePass.vehicleNumber || 'No Vehicle'}</strong>
              </div>
              <div className={styles.detailGroup}>
                <label>Generated Date</label>
                <strong>{new Date(gatePass.validFrom).toLocaleDateString()}</strong>
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <div className={styles.detailGroup}>
                <label>Pass Type</label>
                <strong>{gatePass.type}</strong>
              </div>
              <div className={styles.detailGroup}>
                <label>Status</label>
                <div>
                  <span className={`${styles.statusBadge} ${isRevoked ? styles.statusRevoked : styles.statusActive}`}>
                    <span style={{ fontSize: '10px' }}>●</span> {gatePass.status}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.actions}>
          <button className={styles.btnSecondary} onClick={handleDownloadQR} disabled={isRevoked}>
            <Download size={18} /> Download QR
          </button>
          <button className={styles.btnPrimary} onClick={() => setShowModal(true)} disabled={isRevoked}>
            <Maximize2 size={18} /> Show Full Pass
          </button>
        </div>
      </div>

      {showModal && !isRevoked && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h2>SANSKRUTI</h2>
              <h2 style={{ fontSize: '18px', color: '#475569', fontWeight: 600 }}>SHUBH AANGAN</h2>
            </div>
            
            <div className={styles.modalQr}>
              <QRCodeSVG 
                value={gatePass.qrToken} 
                size={220} 
                level="H"
                includeMargin={true}
              />
            </div>
            
            <div className={styles.modalPassId}>{gatePass.passId}</div>
            
            <div className={styles.modalUser}>
              <h3>{gatePass.resident?.fullName || gatePass.name}</h3>
              <p>Flat {gatePass.flat} • Block {gatePass.block || 'N/A'}</p>
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '32px' }}>
              <span className={`${styles.statusBadge} ${styles.statusActive}`}>
                <span style={{ fontSize: '10px' }}>●</span> Active Pass
              </span>
            </div>
            
            <p className={styles.modalTip}>Show this QR at the society main gate</p>
            
            <button className={styles.btnCloseModal} onClick={() => setShowModal(false)}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyGatePass;
